import { useState, useEffect, useCallback } from "react";
import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { ChevronLeft, ChevronRight, Phone, Camera } from "lucide-react";
import { useJobs } from "@/hooks/useJobs";

export default function RecentJobs() {
  const { items: jobs } = useJobs();
  const [i, setI] = useState(0);
  const n = jobs.length;

  const go = useCallback((delta) => setI((v) => (v + delta + Math.max(1, n)) % Math.max(1, n)), [n]);

  useEffect(() => {
    if (n < 2) return;
    const t = setInterval(() => go(1), 6500);
    return () => clearInterval(t);
  }, [go, n]);

  useEffect(() => {
    // If the jobs list changes and current index falls out of range, reset
    if (i >= n) setI(0);
  }, [i, n]);

  if (n === 0) return null;
  const job = jobs[i];

  return (
    <section id="recent-jobs" className="relative bg-black py-24 sm:py-32 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 right-0 w-[560px] h-[420px] opacity-30"
        style={{ background: "radial-gradient(ellipse at center, rgba(250,204,21,0.16) 0%, rgba(0,0,0,0) 70%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 font-display uppercase tracking-[0.32em] text-yellow-400 text-xs">
              <Camera className="h-4 w-4" strokeWidth={3} /> Recent Recovery Jobs
            </div>
            <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Real trucks. Real jobs. <span className="text-yellow-400">Real customers.</span>
            </h2>
            <p className="mt-5 text-zinc-400 text-base sm:text-lg font-light max-w-2xl leading-relaxed">
              A rotating look at recent work from our team across Kirkland, Bellevue and the Eastside — from dealer flatbeds to late-night roadside calls. Updated as we go.
            </p>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="recent-jobs-call-btn"
            className="hidden lg:inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-3.5 rounded-full lux-glow-yellow transition-colors"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> {PHONE_DISPLAY}
          </a>
        </div>

        <div
          data-testid="recent-jobs-slideshow"
          className="grid lg:grid-cols-12 gap-6 items-stretch"
        >
          {/* Main slide */}
          <div className="lg:col-span-8 relative lux-glass-strong rounded-2xl overflow-hidden">
            <div className="relative aspect-[16/10] w-full overflow-hidden">
              {jobs.map((p, idx) => (
                <img
                  key={p.src}
                  src={p.src}
                  alt={p.alt}
                  data-testid={`recent-jobs-image-${idx}`}
                  width="1600"
                  height="1000"
                  decoding="async"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    idx === i ? "opacity-100" : "opacity-0"
                  }`}
                  style={{ filter: "brightness(1.08) contrast(1.05) saturate(1.08)" }}
                  loading={idx === 0 ? "eager" : "lazy"}
                />
              ))}
              {/* Soft gradient bottom overlay only (image stays bright) */}
              <div
                aria-hidden="true"
                className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none"
                style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 100%)" }}
              />
              {/* Watermark logo */}
              <div className="absolute top-3 left-3 lux-glass rounded-lg p-1.5 h-14 w-14 flex items-center justify-center">
                <img src="/images/logo.png" alt="BM Towing logo watermark" width="56" height="56" decoding="async" loading="lazy" className="max-h-full max-w-full object-contain" />
              </div>
              {/* Controls */}
              <button
                type="button"
                onClick={() => go(-1)}
                data-testid="recent-jobs-prev"
                aria-label="Previous job"
                className="absolute left-3 top-1/2 -translate-y-1/2 lux-glass hover:border-yellow-400/50 text-yellow-400 rounded-full p-2.5 transition-colors"
              >
                <ChevronLeft className="h-5 w-5" strokeWidth={3} />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                data-testid="recent-jobs-next"
                aria-label="Next job"
                className="absolute right-3 top-1/2 -translate-y-1/2 lux-glass hover:border-yellow-400/50 text-yellow-400 rounded-full p-2.5 transition-colors"
              >
                <ChevronRight className="h-5 w-5" strokeWidth={3} />
              </button>
              {/* Bottom caption strip */}
              <div className="absolute inset-x-0 bottom-0 px-6 pb-6 pt-8">
                <div className="font-display font-black uppercase text-white text-xl sm:text-2xl leading-tight tracking-tight">
                  {job.title}
                </div>
                <div className="text-zinc-300 text-sm mt-1 font-light">{job.description}</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {job.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] font-display uppercase tracking-widest lux-glass text-yellow-400 border border-yellow-400/40 px-2.5 py-1 rounded-full"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Thumbnails column */}
          <div className="lg:col-span-4 grid grid-cols-4 lg:grid-cols-2 gap-3">
            {jobs.slice(0, 8).map((p, idx) => (
              <button
                type="button"
                key={p.src}
                onClick={() => setI(idx)}
                data-testid={`recent-jobs-thumb-${idx}`}
                aria-label={p.title}
                className={`relative aspect-square w-full overflow-hidden rounded-xl border transition-colors ${
                  idx === i ? "border-yellow-400/70" : "border-white/10 hover:border-white/30"
                }`}
              >
                <img
                  src={p.src}
                  alt={p.alt}
                  width="200"
                  height="200"
                  decoding="async"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: "brightness(1.05)" }}
                />
                {idx === i && (
                  <span className="absolute inset-0 ring-2 ring-inset ring-yellow-400 rounded-xl pointer-events-none" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 text-xs text-zinc-500 font-display uppercase tracking-[0.28em]">
          Real photos from real BM Towing jobs. No stock imagery. No AI.
        </div>
      </div>
    </section>
  );
}
